import React, { useCallback, useMemo, useRef, useState } from 'react';
import { zip } from 'fflate';
import {
  Upload,
  Download,
  X,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Maximize2,
  Minimize2,
} from 'lucide-react';

// ── Megapiksel Sınırı Uydur ──────────────────────────────────────────────────
// Müşteri görseli yükler; genişlik × yükseklik megapiksel sınırını (varsayılan
// 25 MP) aşıyorsa, görsel EN-BOY ORANI KORUNARAK uniform küçültülür (asla
// bozulmaz/sıkışmaz). Sınırın altındaki görsellere dokunulmaz; orijinali aynen
// indirilebilir. Tamamı tarayıcıda yapılır — sunucuya yüklenmez.

const MP = 1_000_000;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Görsel yüklenemedi')); };
    img.src = url;
  });
}

function bytesPretty(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function mpPretty(w, h) {
  return `${((w * h) / MP).toFixed(1)} MP`;
}

function uid() {
  return Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
}

// Hedef boyutu hesapla: piksel sayısı (w*h) limiti aşıyorsa oranı koruyarak küçült.
// Opsiyonel uzun-kenar limiti varsa onu da uygula. floor + doğrulama ile sonuç
// HER ZAMAN limitlerin altında kalır.
function computeTarget(w, h, maxPixels, maxEdge) {
  let nw = w;
  let nh = h;

  // 1) Megapiksel (alan) limiti
  if (maxPixels > 0 && nw * nh > maxPixels) {
    const s = Math.sqrt(maxPixels / (nw * nh));
    nw = Math.max(1, Math.floor(nw * s));
    nh = Math.max(1, Math.floor(nh * s));
  }

  // 2) Opsiyonel uzun kenar (px) limiti — oran korunur
  if (maxEdge > 0 && Math.max(nw, nh) > maxEdge) {
    const s = maxEdge / Math.max(nw, nh);
    nw = Math.max(1, Math.floor(nw * s));
    nh = Math.max(1, Math.floor(nh * s));
  }

  // 3) Güvenlik: yuvarlama sonrası hâlâ aşıyorsa kademeli kıs
  let guard = 0;
  while (maxPixels > 0 && nw * nh > maxPixels && guard < 8) {
    const s = Math.sqrt(maxPixels / (nw * nh));
    nw = Math.max(1, Math.floor(nw * s));
    nh = Math.max(1, Math.floor(nh * s));
    guard += 1;
  }

  const changed = nw !== w || nh !== h;
  return { w: nw, h: nh, changed };
}

// Yüksek kaliteli küçültme — büyük oran düşüşlerinde tek adımda yumuşaklık
// kaybolur; kademeli (her adımda en fazla yarıya) küçültme ile keskinlik korunur.
async function resizeImage(img, targetW, targetH, mime, quality) {
  let curW = img.naturalWidth || img.width;
  let curH = img.naturalHeight || img.height;

  let canvas = document.createElement('canvas');
  canvas.width = curW;
  canvas.height = curH;
  let ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, curW, curH);

  // Kademeli yarılama
  while (curW > targetW * 2 || curH > targetH * 2) {
    const nextW = Math.max(targetW, Math.round(curW / 2));
    const nextH = Math.max(targetH, Math.round(curH / 2));
    const step = document.createElement('canvas');
    step.width = nextW;
    step.height = nextH;
    const sctx = step.getContext('2d');
    sctx.imageSmoothingEnabled = true;
    sctx.imageSmoothingQuality = 'high';
    sctx.drawImage(canvas, 0, 0, curW, curH, 0, 0, nextW, nextH);
    canvas = step;
    ctx = sctx;
    curW = nextW;
    curH = nextH;
  }

  // Son adım — tam hedef
  if (curW !== targetW || curH !== targetH) {
    const fin = document.createElement('canvas');
    fin.width = targetW;
    fin.height = targetH;
    const fctx = fin.getContext('2d');
    fctx.imageSmoothingEnabled = true;
    fctx.imageSmoothingQuality = 'high';
    fctx.drawImage(canvas, 0, 0, curW, curH, 0, 0, targetW, targetH);
    canvas = fin;
  }

  const dataUrl = canvas.toDataURL(mime, quality);
  const blob = await new Promise((res) => canvas.toBlob((b) => res(b), mime, quality));
  return { dataUrl, blob };
}

export default function MegapixelFit() {
  const [items, setItems] = useState([]); // [{ id, file, url, w, h, status, dataUrl, blob, outW, outH, outMime, changed, error }]
  const [maxMp, setMaxMp] = useState(25);          // megapiksel limiti
  const [maxEdge, setMaxEdge] = useState('');       // opsiyonel uzun kenar (px)
  const [format, setFormat] = useState('auto');     // auto | jpeg | png
  const [quality, setQuality] = useState(92);       // JPEG/WebP kalitesi
  const [running, setRunning] = useState(false);
  const [zipBuilding, setZipBuilding] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const maxPixels = useMemo(() => Math.max(0, Number(maxMp) || 0) * MP, [maxMp]);
  const maxEdgePx = useMemo(() => Math.max(0, parseInt(maxEdge, 10) || 0), [maxEdge]);

  // ── Dosya ekle ──────────────────────────────────────────────────────────────
  const addFiles = useCallback(async (filesList) => {
    const fileArr = Array.from(filesList || []);
    const valid = fileArr.filter((f) => /^image\//i.test(f.type));
    if (!valid.length) { setError('Sadece görsel dosyaları kabul edilir.'); return; }
    setError('');
    const newItems = await Promise.all(valid.map(async (f) => {
      try {
        const { img, url } = await loadImage(f);
        return {
          id: uid(), file: f, url,
          w: img.naturalWidth || img.width, h: img.naturalHeight || img.height,
          img, status: 'idle', dataUrl: '', blob: null,
          outW: 0, outH: 0, outMime: '', changed: false, error: '',
        };
      } catch (_) { return null; }
    }));
    setItems((prev) => [...prev, ...newItems.filter(Boolean)]);
  }, []);

  const onFileInputChange = (e) => {
    const fs = e.target.files;
    if (fs && fs.length) addFiles(fs);
    e.target.value = '';
  };
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current += 1; setIsDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current -= 1; if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false); } };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current = 0; setIsDragging(false); const fs = e.dataTransfer?.files; if (fs && fs.length) addFiles(fs); };

  const removeItem = (id) => {
    setItems((prev) => {
      const it = prev.find((x) => x.id === id);
      if (it?.url) URL.revokeObjectURL(it.url);
      return prev.filter((x) => x.id !== id);
    });
  };

  const clearAll = () => {
    items.forEach((it) => { if (it.url) URL.revokeObjectURL(it.url); });
    setItems([]);
    setError('');
  };

  // ── İşle ──────────────────────────────────────────────────────────────────
  const process = useCallback(async () => {
    if (running) return;
    if (!items.length) { setError('Önce en az bir görsel yükle.'); return; }
    if (maxPixels <= 0) { setError('Geçerli bir megapiksel limiti gir.'); return; }
    setError('');
    setRunning(true);

    const setStatus = (id, patch) => setItems((prev) => prev.map((it) => it.id === id ? { ...it, ...patch } : it));

    for (const it of items) {
      setStatus(it.id, { status: 'processing' });
      try {
        const target = computeTarget(it.w, it.h, maxPixels, maxEdgePx);

        // Çıktı formatı
        let outMime;
        if (format === 'jpeg') outMime = 'image/jpeg';
        else if (format === 'png') outMime = 'image/png';
        else outMime = it.file.type === 'image/png' ? 'image/png' : 'image/jpeg'; // auto

        const q = outMime === 'image/png' ? undefined : quality / 100;

        if (!target.changed && (format === 'auto' || outMime === it.file.type)) {
          // Zaten sınır altında ve format değişmiyor → orijinali aynen kullan (pristine)
          setStatus(it.id, {
            status: 'done', dataUrl: it.url, blob: it.file,
            outW: it.w, outH: it.h, outMime: it.file.type, changed: false,
          });
        } else {
          const { dataUrl, blob } = await resizeImage(it.img, target.w, target.h, outMime, q);
          setStatus(it.id, {
            status: 'done', dataUrl, blob,
            outW: target.w, outH: target.h, outMime, changed: target.changed,
          });
        }
      } catch (e) {
        setStatus(it.id, { status: 'error', error: e?.message || 'İşlenemedi' });
      }
    }
    setRunning(false);
  }, [running, items, maxPixels, maxEdgePx, format, quality]);

  // ── İndirme ─────────────────────────────────────────────────────────────────
  const fileNameFor = (it) => {
    const base = (it.file.name || 'image').replace(/\.[a-z0-9]+$/i, '');
    const ext = it.outMime === 'image/png' ? 'png' : (it.outMime === 'image/webp' ? 'webp' : 'jpg');
    const suffix = it.changed ? `_${it.outW}x${it.outH}` : '';
    return `${base}${suffix}.${ext}`;
  };

  const downloadOne = (it) => {
    if (!it.blob) return;
    const url = URL.createObjectURL(it.blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileNameFor(it); a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllZip = async () => {
    if (zipBuilding) return;
    const ready = items.filter((it) => it.blob && it.status === 'done');
    if (!ready.length) { setError('İndirilecek hazır çıktı yok.'); return; }
    setError('');
    setZipBuilding(true);
    try {
      const fileMap = {};
      const used = {};
      for (const it of ready) {
        let candidate = fileNameFor(it);
        let n = 1;
        while (used[candidate]) {
          const base = candidate.replace(/\.[a-z0-9]+$/i, '');
          const ext = candidate.split('.').pop();
          candidate = `${base}_${n}.${ext}`; n += 1;
        }
        used[candidate] = true;
        fileMap[candidate] = new Uint8Array(await it.blob.arrayBuffer());
      }
      const zipped = await new Promise((resolve, reject) => {
        zip(fileMap, { level: 6 }, (err, data) => { if (err) reject(err); else resolve(data); });
      });
      const blob = new Blob([zipped], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-');
      a.href = url; a.download = `gorsel-kucult_${ts}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'ZIP oluşturulamadı.');
    } finally {
      setZipBuilding(false);
    }
  };

  const stats = useMemo(() => ({
    total: items.length,
    done: items.filter((it) => it.status === 'done').length,
    processing: items.filter((it) => it.status === 'processing').length,
    errored: items.filter((it) => it.status === 'error').length,
    resized: items.filter((it) => it.status === 'done' && it.changed).length,
  }), [items]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold ring-1 ring-blue-200 mb-3">
            <Minimize2 className="w-3.5 h-3.5" />
            Görsel Küçültücü · Megapiksel Sınırı
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Görseli Megapiksel Sınırına Uydur</h1>
          <p className="mt-2 text-gray-600 text-sm max-w-2xl leading-relaxed">
            "25 megapiksel (genişlik × yükseklik) sınırını aşıyor" hatası alan görselleri yükle; sistem her birini
            <strong> en-boy oranını koruyarak</strong> sınırın altına küçültür — görsel <strong>asla sıkışmaz / bozulmaz</strong>.
            Zaten sınır altında olanlara dokunulmaz. Her şey tarayıcında yapılır, dosyalar hiçbir yere yüklenmez.
          </p>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* MP limit */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Megapiksel Limiti</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="1" max="200" step="1" value={maxMp}
                  onChange={(e) => setMaxMp(e.target.value)}
                  className="w-20 px-2 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
                />
                <span className="text-xs text-gray-500">MP (G×Y)</span>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Çoğu platform için 25 MP. Örn: 5000×5000 = 25 MP.</p>
            </div>

            {/* Optional max edge */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Uzun Kenar (ops.)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="0" step="100" value={maxEdge} placeholder="—"
                  onChange={(e) => setMaxEdge(e.target.value)}
                  className="w-24 px-2 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
                />
                <span className="text-xs text-gray-500">px</span>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Boş bırak = sadece megapiksel.</p>
            </div>

            {/* Format */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Format</label>
              <div className="flex gap-1">
                <button onClick={() => setFormat('auto')} className={`flex-1 text-xs font-medium px-2 py-2 rounded-lg ${format === 'auto' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Otomatik</button>
                <button onClick={() => setFormat('jpeg')} className={`flex-1 text-xs font-medium px-2 py-2 rounded-lg ${format === 'jpeg' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>JPEG</button>
                <button onClick={() => setFormat('png')} className={`flex-1 text-xs font-medium px-2 py-2 rounded-lg ${format === 'png' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>PNG</button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Otomatik = orijinal format korunur.</p>
            </div>

            {/* Quality */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Kalite (JPEG)</label>
              <div className="flex items-center gap-2">
                <input
                  type="range" min="60" max="100" value={quality} disabled={format === 'png'}
                  onChange={(e) => setQuality(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-xs font-medium text-gray-700 w-8 text-right">{quality}</span>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">PNG kayıpsızdır, kalite uygulanmaz.</p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
            <button
              onClick={process}
              disabled={running || !items.length}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Minimize2 className="w-4 h-4" />}
              {running ? 'Küçültülüyor…' : `Küçült (${items.length})`}
            </button>
            <button
              onClick={downloadAllZip}
              disabled={zipBuilding || !stats.done}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
            >
              {zipBuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {zipBuilding ? 'ZIP hazırlanıyor…' : `Tümünü ZIP İndir (${stats.done})`}
            </button>
            {items.length > 0 && (
              <button onClick={clearAll} className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-600 ml-auto">
                <Trash2 className="w-3.5 h-3.5" /> Tümünü temizle
              </button>
            )}
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}
          className={`bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-8 mb-5 text-center cursor-pointer transition-all ${isDragging ? 'ring-2 ring-blue-400 bg-blue-50' : 'hover:bg-gray-50'}`}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
          <p className="text-sm font-medium text-gray-700">Görselleri buraya sürükle bırak</p>
          <p className="text-xs text-gray-500 mt-1">veya tıkla — birden fazla görsel seçebilirsin (PNG / JPG)</p>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onFileInputChange} className="hidden" />
        </div>

        {/* Stats */}
        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mb-4">
            <span><strong>{stats.total}</strong> görsel</span>
            {stats.done > 0 && (<><span className="text-gray-400">·</span><span className="text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />{stats.done} hazır</span></>)}
            {stats.resized > 0 && (<><span className="text-gray-400">·</span><span className="text-blue-700"><Minimize2 className="w-3.5 h-3.5 inline mr-1" />{stats.resized} küçültüldü</span></>)}
            {stats.processing > 0 && (<><span className="text-gray-400">·</span><span className="text-blue-700"><Loader2 className="w-3.5 h-3.5 inline mr-1 animate-spin" />{stats.processing} işleniyor</span></>)}
            {stats.errored > 0 && (<><span className="text-gray-400">·</span><span className="text-red-700"><AlertCircle className="w-3.5 h-3.5 inline mr-1" />{stats.errored} hatalı</span></>)}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-5 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
          </div>
        )}

        {/* Items */}
        {items.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((it) => {
              const over = maxPixels > 0 && it.w * it.h > maxPixels;
              return (
                <div key={it.id} className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm overflow-hidden">
                  <div className="relative bg-gray-50 flex items-center justify-center aspect-square">
                    <img src={it.dataUrl || it.url} alt={it.file.name} className="w-full h-full object-contain" />
                    <button onClick={() => removeItem(it.id)} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 ring-1 ring-gray-200 hover:bg-red-50 flex items-center justify-center">
                      <X className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    {it.status === 'processing' && (
                      <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                      </div>
                    )}
                    {/* Durum rozeti */}
                    {it.status === 'idle' && (
                      <span className={`absolute bottom-1.5 left-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${over ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {over ? 'Sınır aşılıyor' : 'Uygun'}
                      </span>
                    )}
                    {it.status === 'done' && (
                      <span className={`absolute bottom-1.5 left-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${it.changed ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {it.changed ? 'Küçültüldü' : 'Değişmedi'}
                      </span>
                    )}
                  </div>
                  <div className="px-3 py-2 border-t border-gray-100">
                    <p className="text-[11px] font-medium text-gray-700 truncate" title={it.file.name}>{it.file.name}</p>
                    {it.status === 'done' && it.changed ? (
                      <p className="text-[10px] text-gray-400">
                        {it.w}×{it.h} ({mpPretty(it.w, it.h)}) → <strong className="text-blue-600">{it.outW}×{it.outH}</strong> ({mpPretty(it.outW, it.outH)})
                      </p>
                    ) : (
                      <p className="text-[10px] text-gray-400">
                        {it.w}×{it.h} · {mpPretty(it.w, it.h)}{it.blob ? ` · ${bytesPretty(it.blob.size)}` : ` · ${bytesPretty(it.file.size)}`}
                      </p>
                    )}
                    {it.status === 'error' && (
                      <p className="text-[10px] text-red-600 flex items-center gap-1 mt-0.5"><AlertCircle className="w-3 h-3" />{it.error}</p>
                    )}
                  </div>
                  <div className="px-3 pb-3">
                    <button
                      onClick={() => downloadOne(it)}
                      disabled={!it.blob || it.status !== 'done'}
                      className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-2 py-1.5 rounded-lg"
                    >
                      <Download className="w-3 h-3" /> İndir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {items.length === 0 && (
          <div className="text-center text-gray-400 text-sm py-10 flex flex-col items-center gap-2">
            <Maximize2 className="w-8 h-8 text-gray-300" />
            Henüz görsel yok. Yukarıdan yükle, ardından "Küçült" e bas.
          </div>
        )}
      </div>
    </div>
  );
}
